/**
 * /admin/people, admin-mode people management (Stripe-style table).
 *
 * Per UX-DESIGN.md: tables with hover rows, kebab menu for row actions,
 * skeleton loading. Inline invite form. Toast for action feedback.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import {
  fetchAdminPeople,
  fetchAccountTags,
  updatePersonTags,
  inviteMember,
  updateMemberRole,
  renameAccountUser,
  deactivateMember,
  reactivateMember,
  deleteInvitation,
  resendInvitation,
  fetchConnectorSets,
  updateConnectorSetMembership,
  fetchAdminRequests,
  fetchAdminConnectors,
  fetchUsageByUser,
  installConnector,
  resolveAccessRequest,
  ApiError,
  type AdminPerson,
  type AdminInvitation,
  type ConnectorSet,
  type AdminConnectorInstance,
  type UsageByUserRow,
} from '../lib/api.ts';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { formatTimestamp } from '../lib/format-timestamp.ts';
import { formatLabel } from '../lib/format.ts';
import { TagChipEditor } from '../components/TagChipEditor.tsx';
import { BulkCapByTagModal } from './admin/BulkCapByTagModal.tsx';
import { KebabMenu, type KebabAction } from '../components/KebabMenu.tsx';
// Push 81 (2026-07-23) — ListFilterDropdown passed to <ListViewShell>
// via filter prop.
// Sub-agent KKKKK Track 2 (2026-07-29) — Requests-tab FilterChipGroup
// (Status: Awaiting me / All pending / Historical) collapsed into the
// unified ListFilterDropdown; direct FilterChipGroup import dropped.
import {
  type ListFilterDimension,
} from '../components/ListFilterDropdown.tsx';
import { ListViewShell } from '../components/ListViewShell.tsx';
import { ListRow } from '../components/ListRow.tsx';
import { RecordLink } from '../components/RecordLink.tsx';
import {
  InvitationStatusPill,
  Pill,
  RoleBadge,
  type PillKind,
} from '../components/StatusPill.tsx';
import { useToast } from '../components/Toast.tsx';
import { ObjectIcon } from '../components/ObjectIcon.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { useDelayedFlag } from '../hooks/useDelayedFlag.ts';
import { ConfirmDialog } from '../components/ConfirmDialog.tsx';
import { Checkbox } from '../components/Checkbox.tsx';
import { LoadingButton } from '../components/LoadingButton.tsx';
// Push 5-F (2026-07-11): consolidate SkeletonTable primitive to Skeleton.tsx
// (previously duplicated in People.tsx, MyActivity.tsx, ActivityDispatches.tsx).
import { SkeletonTable } from '../components/Skeleton.tsx';

/**
 * Sub-agent UUUUU (2026-07-31) — small relative-time formatter for the
 * "Last active" column. Not worth a top-level util helper — only used
 * here + potentially by future admin list surfaces (revisit if it grows
 * a second caller). Keeps the format terse: "2h ago", "yesterday",
 * "3 days ago", "2 weeks ago", "May 12" (older than 60 days).
 */
function formatRelativePeople(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `${diffH}h ago`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'yesterday';
  if (diffD < 14) return `${diffD} days ago`;
  if (diffD < 60) return `${Math.round(diffD / 7)} weeks ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Sub-agent UUUUU (2026-07-31) — relative "expires in Nd" formatter for
 * the Invitations list. Symmetric to formatRelativePeople but goes forward
 * in time. Renders "Expired" when past.
 */
function formatExpiresInPeople(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return 'Expired';
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 60) return `Expires in ${diffMin}m`;
  const diffH = Math.round(diffMin / 60);
  if (diffH < 24) return `Expires in ${diffH}h`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return 'Expires tomorrow';
  if (diffD < 14) return `Expires in ${diffD} days`;
  return `Expires ${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

/**
 * Hero-metric tile for the People Users tab. Terse — label + big value +
 * optional caption. Tone signals attention (warn = rust border) but stays
 * compact enough to sit above the list without pushing the table off-screen.
 */
function PeopleHeroMetric({
  label,
  value,
  caption,
  tone = 'default',
}: {
  label: string;
  value: string;
  caption?: string;
  tone?: 'default' | 'warn';
}) {
  const borderColor =
    tone === 'warn' ? 'rgba(184, 90, 34, 0.35)' : 'var(--border)';
  const valueColor =
    tone === 'warn' ? 'rgb(184, 90, 34)' : 'var(--olytic-obsidian)';
  return (
    <div
      className="card"
      style={{
        padding: '10px 12px',
        borderColor,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        minHeight: 68,
      }}
    >
      <div
        className="muted"
        style={{
          fontSize: 10.5,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 700,
          color: valueColor,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {caption !== undefined && (
        <div
          className="muted"
          style={{ fontSize: 11, lineHeight: 1.3, marginTop: 1 }}
        >
          {caption}
        </div>
      )}
    </div>
  );
}

export function People() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [members, setMembers] = useState<AdminPerson[] | null>(null);
  const [invitations, setInvitations] = useState<AdminInvitation[]>([]);
  // Sub-agent UUUUU (2026-07-31) — per-user usage rows fetched alongside
  // the People list. Powers the Users hero metric strip + per-row
  // "Spend this month" + "Last active" columns without a new endpoint.
  const [usageByUser, setUsageByUser] = useState<UsageByUserRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  // Push #84 round 19 (dogfood feedback 2026-06-22) — visible
  // confirmation on resend. Tracks invitation IDs whose email JUST got
  // resent so the row can show "Email sent ✓" alongside the status pill
  // for a few seconds. Toasts alone were easy to miss (auto-dismiss);
  // this gives the operator inline reassurance that the action landed.
  const [recentlyResent, setRecentlyResent] = useState<Set<string>>(new Set());

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'user'>('user');
  // Push 70b (D-198 wallet collapse): invite-time wallet_policy picker
  // retired. Every user joins on team-pot; admin tunes tags + spend caps
  // after the invite is accepted.
  const [inviting, setInviting] = useState(false);
  const showSkeleton = useDelayedFlag(members === null);

  // Inline-edit per Ean 2026-06-09: click the name → input + Save; role
  // dropdown swaps for direct selection instead of kebab → confirm.
  // Search filters across name + email so 50-user orgs are tractable.
  const [search, setSearch] = useState('');
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<string>('');

  // Round 6.5 Item 31 — bulk-select state. Selected IDs across the active
  // users table. Bulk PS assign + bulk role-change land here; sticky bar
  // appears when selectedIds.size > 0. Fires per-user serially with
  // progress indication; partial failures surface via Toast detail.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [permissionSets, setPermissionSets] = useState<ConnectorSet[]>([]);
  const [bulkPsId, setBulkPsId] = useState<string>('');
  const [bulkRoleTarget, setBulkRoleTarget] = useState<'admin' | 'user' | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

  // Push 70b (D-198 wallet collapse): topupTarget + bulk-top-up retired.
  // Individual allocation is gone (Migration 0158); per-user knob is the
  // spend-cap, edited from PersonDetail's CreditsPoolCard.

  // Push 4-1aj (2026-07-10) — branded ConfirmDialog replaces the last 3
  // window.confirm() sites on People.tsx: deactivate member, promote to
  // owner, delete invite. Each dialog carries its target row via state
  // hoisted here; the actual mutation runs after user confirms.
  const [deactivateTarget, setDeactivateTarget] = useState<AdminPerson | null>(null);
  const [promoteOwnerTarget, setPromoteOwnerTarget] = useState<AdminPerson | null>(null);
  const [deleteInviteTarget, setDeleteInviteTarget] = useState<AdminInvitation | null>(null);
  const [bulkDeactivateOpen, setBulkDeactivateOpen] = useState(false);

  // Push 70b (D-198) — tag state. Distinct tag values loaded once for
  // typeahead + Tag filter + BulkCapByTagModal. Reloads whenever the
  // People list refetches so newly-created tags surface immediately.
  //
  // Push 70c (D-198): tagFilter seeds from ?tags= URL param so Analytics ›
  // Spend › By tag can deep-link straight to the roster filtered by tag.
  // Comma-separated for multi-tag drill-through (rare but supported).
  const [accountTags, setAccountTags] = useState<string[]>([]);
  const [tagFilter, setTagFilter] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    const raw = new URLSearchParams(window.location.search).get('tags');
    if (raw === null || raw === '') return new Set();
    return new Set(
      raw
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0),
    );
  });
  const [inlineTagEditRow, setInlineTagEditRow] = useState<string | null>(null);
  const [bulkCapModalOpen, setBulkCapModalOpen] = useState(false);

  // Push 79 (2026-07-23) — filter dropdown state (Role + spend limit
  // + tags) persists to localStorage per PATTERNS.md § D "List view
  // shape". Status (Active/Inactive) still lives on the URL so external
  // deep links keep resolving to the right slice.
  //
  // Push 81 (2026-07-23): storage key renamed to the surface-scoped
  // `list-view:{surface-name}` convention now required by <ListViewShell>.
  // Legacy `people-filter` key read as a fallback so existing users keep
  // their preference.
  //
  // Sub-agent RRRRR (2026-07-31) — the Active/Inactive preset pillbox
  // (`viewSelector`) is RETIRED. The Push 79 "inbox mode carve-out" is
  // walked back per Ean's list-view unification directive: every preset
  // filter routes through the unified Filter dropdown. Status becomes a
  // Status dimension alongside Role / Spend limit / Tags; the URL param
  // `?status=inactive` continues to be written by the Filter dropdown
  // onChange so bookmarks + deep links stay stable.
  //
  // localStorage schema: JSON { role: 'all'|'owner'|'admin'|'user',
  //                             cap:  'all'|'custom'|'unlimited' }
  const LS_PEOPLE_FILTER = 'list-view:admin-people:filter';
  const LS_PEOPLE_FILTER_LEGACY = 'people-filter';
  type PeopleRoleFilter = 'all' | 'owner' | 'admin' | 'user';
  type PeopleCapFilter = 'all' | 'custom' | 'unlimited';

  const [roleFilter, setRoleFilter] = useState<PeopleRoleFilter>(() => {
    if (typeof window === 'undefined') return 'all';
    try {
      let raw = window.localStorage.getItem(LS_PEOPLE_FILTER);
      if (raw === null) raw = window.localStorage.getItem(LS_PEOPLE_FILTER_LEGACY);
      if (raw !== null) {
        const parsed = JSON.parse(raw) as { role?: string };
        if (
          parsed.role === 'all' ||
          parsed.role === 'owner' ||
          parsed.role === 'admin' ||
          parsed.role === 'user'
        ) {
          return parsed.role;
        }
      }
    } catch {
      /* ignore */
    }
    return 'all';
  });
  const [capFilter, setCapFilter] = useState<PeopleCapFilter>(() => {
    if (typeof window === 'undefined') return 'all';
    try {
      let raw = window.localStorage.getItem(LS_PEOPLE_FILTER);
      if (raw === null) raw = window.localStorage.getItem(LS_PEOPLE_FILTER_LEGACY);
      if (raw !== null) {
        const parsed = JSON.parse(raw) as { cap?: string };
        if (parsed.cap === 'all' || parsed.cap === 'custom' || parsed.cap === 'unlimited') {
          return parsed.cap;
        }
      }
    } catch {
      /* ignore */
    }
    return 'all';
  });

  function persistPeopleFilter(patch: {
    role?: PeopleRoleFilter;
    cap?: PeopleCapFilter;
  }): void {
    try {
      const merged = {
        role: patch.role ?? roleFilter,
        cap: patch.cap ?? capFilter,
      };
      window.localStorage.setItem(LS_PEOPLE_FILTER, JSON.stringify(merged));
    } catch {
      /* ignore */
    }
  }

  async function reload() {
    try {
      const d = await fetchAdminPeople();
      setMembers(d.members);
      setInvitations(d.invitations);
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not load people.',
      });
    }
    // Sub-agent UUUUU (2026-07-31) — best-effort usage fetch, this-month
    // scoped. Failure is silent (metric strip + row columns just render
    // zero / dash); we never want a usage-endpoint failure to block the
    // People page from loading.
    try {
      const usage = await fetchUsageByUser('this_month');
      setUsageByUser(usage.rows);
    } catch {
      setUsageByUser([]);
    }
  }

  async function reloadTags() {
    try {
      const res = await fetchAccountTags();
      setAccountTags(res.values);
    } catch {
      // Silent — typeahead just shows an empty list on failure.
    }
  }

  useEffect(() => {
    void reload();
    void reloadTags();
    // Round 6.5 Item 31 — load permission sets once for the bulk PS picker.
    // Errors are surface-silent (the bulk PS action just shows an empty
    // dropdown) so a PS-fetch failure doesn't block the People page.
    void (async () => {
      try {
        const res = await fetchConnectorSets();
        setPermissionSets(res.sets);
      } catch {
        // Silent — bulk PS picker just shows empty if the fetch fails.
      }
    })();
  }, []);

  if (user === null) return null;
  const isOwner = user.role === 'owner';

  // Round 6.5 Item 31 — toggle helpers + bulk action runner.
  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelected() {
    setSelectedIds(new Set());
  }

  /**
   * Run a per-user mutation serially across selectedIds with progress
   * tracking + per-failure capture. On partial failure, surface affected
   * display names via Toast so the operator knows which to retry.
   */
  async function runBulk(
    action: (m: AdminPerson) => Promise<void>,
    successLabel: string,
  ) {
    if (members === null || selectedIds.size === 0) return;
    const targets = members.filter((m) => selectedIds.has(m.account_user_id));
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: targets.length });
    const failed: string[] = [];
    for (let i = 0; i < targets.length; i++) {
      const m = targets[i]!;
      try {
        await action(m);
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'failed';
        failed.push(`${m.display_name} (${msg})`);
      }
      setBulkProgress({ done: i + 1, total: targets.length });
    }
    setBulkBusy(false);
    setBulkProgress(null);
    if (failed.length === 0) {
      showToast({ kind: 'success', message: `${successLabel} (${targets.length}).` });
      clearSelected();
    } else if (failed.length === targets.length) {
      showToast({
        kind: 'error',
        message: `All ${targets.length} updates failed. Examples: ${failed.slice(0, 2).join('; ')}.`,
      });
    } else {
      showToast({
        kind: 'error',
        message: `${targets.length - failed.length} of ${targets.length} updated. Failed: ${failed.join('; ')}.`,
      });
    }
    await reload();
  }

  async function handleBulkAssignToPS() {
    if (bulkPsId === '') return;
    const psName =
      permissionSets.find((p) => p.id === bulkPsId)?.name ?? 'permission set';
    const ids = Array.from(selectedIds);
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: ids.length });
    const failed: string[] = [];
    const memberById = new Map(
      (members ?? []).map((m) => [m.account_user_id, m] as const),
    );
    // PS membership endpoint accepts an array — but per-user feedback is
    // valuable, so we issue add_users one user at a time to mirror the
    // serial + progress pattern used for role changes.
    for (let i = 0; i < ids.length; i++) {
      const id = ids[i]!;
      try {
        await updateConnectorSetMembership(bulkPsId, { add_users: [id] });
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : 'failed';
        const name = memberById.get(id)?.display_name ?? id;
        failed.push(`${name} (${msg})`);
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkBusy(false);
    setBulkProgress(null);
    if (failed.length === 0) {
      showToast({
        kind: 'success',
        message: `Added ${ids.length} to ${psName}.`,
      });
      clearSelected();
      setBulkPsId('');
    } else {
      showToast({
        kind: 'error',
        message: `${ids.length - failed.length} of ${ids.length} added. Failed: ${failed.join('; ')}.`,
      });
    }
    await reload();
  }

  async function confirmBulkRoleChange() {
    if (bulkRoleTarget === null) return;
    const target = bulkRoleTarget;
    setBulkRoleTarget(null);
    await runBulk(async (m) => {
      if (m.role === target) return;
      await updateMemberRole(m.account_user_id, target);
    }, `Role changed to ${target}`);
  }

  // Push 70b (D-198): confirmBulkTopup retired. Individual allocation is
  // gone; the per-user knob is the spend-cap. Bulk spend-cap sets happen
  // via BulkCapByTagModal (tag-scoped selection) — see header CTA below.

  // Push 4-1ak (2026-07-10) — bulk deactivate. Fires the existing
  // /api/admin/people/deactivate endpoint per user. Owner + self rows are
  // skipped defensively (deactivateMember server-rejects those anyway).
  // Post-run, People list refetches so status pills flip to Deactivated.
  async function confirmBulkDeactivate() {
    setBulkDeactivateOpen(false);
    await runBulk(async (m) => {
      if (m.role === 'owner') return;
      if (m.account_user_id === user!.user_id) return;
      await deactivateMember(m.account_user_id);
    }, `Deactivated`);
  }

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await inviteMember(
        inviteEmail.trim().toLowerCase(),
        inviteRole,
      );
      if (res.email_sent) {
        showToast({ kind: 'success', message: `Invite emailed to ${inviteEmail.trim()}.` });
      } else {
        await navigator.clipboard.writeText(res.invite_url).catch(() => undefined);
        showToast({
          kind: 'success',
          message: 'Invite created. Link copied to your clipboard. Paste it to them.',
        });
      }
      setInviteEmail('');
      setInviteRole('user');
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : "We couldn't send that invite. Try again in a moment.",
      });
    } finally {
      setInviting(false);
    }
  }

  // Kebab actions per row. Push 51 (2026-07-15, Ean bug report) — the row
  // kebab was missing an "Open" primary action + a "Copy email" quick action
  // and Ean couldn't figure out how to click into a user record. Renamed
  // "View details" → "Open" so the label matches the row-click behavior;
  // added "Copy email" as a common ops shortcut. Role changes stay on the
  // inline dropdown per Ean 2026-06-09; rename stays on the inline pencil
  // button. Deactivate / Reactivate remain the lifecycle actions here.
  function buildMemberActions(m: AdminPerson): KebabAction[] {
    const actions: KebabAction[] = [];
    // D-148 universal-detail discipline — every row links to its record
    // page. Available on yourself too (you can see your own record page;
    // rename + lifecycle actions stay self-locked).
    actions.push({
      label: 'Open',
      onSelect: () => navigate(`/admin/people/${m.short_id}`),
    });
    // Push 51 — Copy email one-shot. Available for every row (including
    // yourself) as a common ops shortcut when pinging someone off-platform.
    // Uses navigator.clipboard; falls back to a toast on error.
    if (m.email !== '') {
      actions.push({
        label: 'Copy email',
        onSelect: () => {
          navigator.clipboard
            .writeText(m.email)
            .then(() =>
              showToast({ kind: 'success', message: `Copied ${m.email}.` }),
            )
            .catch(() =>
              showToast({ kind: 'error', message: 'Could not copy email.' }),
            );
        },
      });
    }
    // Push 70b (D-198 wallet collapse): "Top up user" retired. Per-user
    // knob is the spend-cap, edited from PersonDetail's CreditsPoolCard.
    // Bulk spend-cap by tag lives on the header BulkCapByTagModal CTA.
    //
    // Push 71 (Sub-agent JJJ audit): Claude Console reference DNA — the
    // per-user row kebab in "Spend limits by user" carries "Edit limit"
    // and "Remove limit". Junction-symmetric per D-161: the edit lives on
    // the PersonDetail Billing tab (CreditsPoolCard cap editor), so the
    // kebab item deep-links there with the tab focused. The row-inline
    // one-click edit lives on that page too; here we surface the
    // affordance so admins don't have to hunt for "where do I cap this
    // user's spend?"
    actions.push({
      label:
        m.monthly_hard_cap_credits === null ||
        m.monthly_hard_cap_credits === ''
          ? 'Set spend limit'
          : 'Edit spend limit',
      onSelect: () =>
        navigate(`/admin/people/${m.short_id}?tab=billing`),
    });
    if (m.account_user_id === user!.user_id) {
      return actions; // No lifecycle actions on yourself
    }
    if (m.role !== 'owner') {
      if (m.is_active) {
        actions.push({
          label: 'Deactivate',
          danger: true,
          onSelect: () => void handleDeactivate(m),
        });
      } else {
        actions.push({
          label: 'Reactivate',
          onSelect: () => void handleReactivate(m),
        });
      }
    }
    return actions;
  }

  function handleDeactivate(member: AdminPerson) {
    // Push 4-1aj (2026-07-10) — open branded ConfirmDialog instead of
    // window.confirm(). Actual deactivation runs in performDeactivate()
    // after user confirms.
    setDeactivateTarget(member);
  }

  async function performDeactivate(member: AdminPerson) {
    setDeactivateTarget(null);
    setBusy(member.account_user_id);
    try {
      await deactivateMember(member.account_user_id);
      showToast({ kind: 'success', message: `${member.display_name} deactivated.` });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Deactivate failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function handleReactivate(member: AdminPerson) {
    setBusy(member.account_user_id);
    try {
      await reactivateMember(member.account_user_id);
      showToast({ kind: 'success', message: `${member.display_name} reactivated.` });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Reactivate failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function handleSaveName(member: AdminPerson) {
    const next = nameDraft.trim();
    if (next === '' || next === member.display_name) {
      setEditingNameId(null);
      return;
    }
    setBusy(member.account_user_id);
    try {
      await renameAccountUser(member.account_user_id, next);
      showToast({ kind: 'success', message: `Renamed ${member.display_name} to ${next}.` });
      setEditingNameId(null);
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Rename failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  /** Inline role change per Ean 2026-06-09 — swap the kebab confirm flow for
   *  a direct dropdown change. Promote-to-owner stays gated to owners (the
   *  endpoint enforces) but no longer needs three kebab clicks. */
  async function handleInlineRoleChange(member: AdminPerson, newRole: 'owner' | 'admin' | 'user') {
    if (newRole === member.role) return;
    // Push 4-1aj (2026-07-10) — promote-to-owner routes through the branded
    // ConfirmDialog instead of window.confirm(). Non-owner role changes fire
    // directly (unchanged). See performPromoteToOwner() for the confirm arm.
    if (newRole === 'owner') {
      setPromoteOwnerTarget(member);
      return;
    }
    setBusy(member.account_user_id);
    try {
      await updateMemberRole(member.account_user_id, newRole);
      showToast({ kind: 'success', message: `${member.display_name} is now ${newRole}.` });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not update role.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function performPromoteToOwner(member: AdminPerson) {
    setPromoteOwnerTarget(null);
    setBusy(member.account_user_id);
    try {
      await updateMemberRole(member.account_user_id, 'owner');
      showToast({ kind: 'success', message: `${member.display_name} is now owner.` });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not update role.',
      });
    } finally {
      setBusy(null);
    }
  }

  // Push 70b (D-198 wallet collapse): handlePolicyChange retired. Wallet
  // policy is CHECK-locked to 'team_pot' at the DB layer; there's no
  // per-user variant to change. Per-user tuning happens via tags + caps.
  async function handleUpdateTags(member: AdminPerson, next: string[]) {
    const prevTags = member.tags ?? [];
    // Optimistic update on the member row.
    setMembers((prev) =>
      prev === null
        ? prev
        : prev.map((m) =>
            m.account_user_id === member.account_user_id
              ? { ...m, tags: next.length === 0 ? null : next }
              : m,
          ),
    );
    try {
      await updatePersonTags(member.account_user_id, next);
      // Refresh the tag-suggestions list so newly-created tags surface
      // for typeahead + the tag filter.
      void reloadTags();
      showToast({
        kind: 'success',
        message: `Tags updated for ${member.display_name}.`,
      });
    } catch (err) {
      // Roll back the optimistic write on failure.
      setMembers((prev) =>
        prev === null
          ? prev
          : prev.map((m) =>
              m.account_user_id === member.account_user_id
                ? {
                    ...m,
                    tags: prevTags.length === 0 ? null : prevTags,
                  }
                : m,
            ),
      );
      showToast({
        kind: 'error',
        message:
          err instanceof ApiError ? err.message : 'Could not update tags.',
      });
    }
  }

  function handleDeleteInvite(inv: AdminInvitation) {
    // Push 4-1aj (2026-07-10) — open branded ConfirmDialog instead of
    // window.confirm(). Actual deletion runs in performDeleteInvite().
    setDeleteInviteTarget(inv);
  }

  async function performDeleteInvite(inv: AdminInvitation) {
    setDeleteInviteTarget(null);
    setBusy(inv.id);
    try {
      await deleteInvitation(inv.id);
      showToast({ kind: 'success', message: `Invite for ${inv.email} deleted.` });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not delete invite.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function handleResendInvite(inv: AdminInvitation) {
    setBusy(inv.id);
    try {
      const res = await resendInvitation(inv.id);
      // Inline "Email sent ✓" badge on the row for 5 seconds, alongside
      // the toast. Toast is the audible confirmation, badge is the
      // visual one — the action button is in a kebab menu so a direct
      // button-state change isn't ergonomic; row-level badge is the
      // next best thing.
      setRecentlyResent((prev) => {
        const next = new Set(prev);
        next.add(inv.id);
        return next;
      });
      setTimeout(() => {
        setRecentlyResent((prev) => {
          const next = new Set(prev);
          next.delete(inv.id);
          return next;
        });
      }, 5000);
      showToast({
        kind: 'success',
        message: res.email_sent
          ? `Invite resent to ${inv.email}.`
          : `Resent. Email delivery pending (check operator log).`,
      });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not resend invite.',
      });
    } finally {
      setBusy(null);
    }
  }

  // Push 4-1e-cleanup-fu2 (2026-07-08): tab shape simplified to 3 tabs —
  // Users (default) · Invitations · Approvals. Prior 4-tab shape carried
  // a separate Deactivated tab which is now merged into Users as a
  // secondary Active/Inactive filter (?status=active|inactive). Approvals
  // renamed from "Awaiting approval" for cleaner naming.
  //
  // Legacy synonyms accepted:
  //   ?tab=everyone → users
  //   ?tab=all → users
  //   ?tab=deactivated → users&status=inactive
  //   ?tab=invites → invitations
  //   ?tab=requests → approvals
  //   ?tab=awaiting-approval → approvals
  //   ?tab=awaiting_approval → approvals
  // Push 70b (D-198): tab key 'approvals' renamed to 'requests' — the
  // approval workflow is gone; the tab now hosts sys__event_log requests
  // (access / suggestion / feedback / support) only. Legacy '?tab=approvals'
  // reads are still accepted (see normalizePeople below) so external
  // links keep working.
  const TAB_KEYS = ['users', 'invitations', 'requests'] as const;
  type PeopleTab = (typeof TAB_KEYS)[number];
  const STATUS_KEYS = ['active', 'inactive'] as const;
  type UsersStatus = (typeof STATUS_KEYS)[number];
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') ?? 'users';
  const rawStatus = searchParams.get('status') ?? '';

  // Normalize into { tab, status }. Legacy `?tab=deactivated` promotes to
  // `?tab=users&status=inactive` implicitly at read time so old bookmarks
  // still land on the deactivated roster.
  function normalizePeople(rawT: string, rawS: string): { tab: PeopleTab; status: UsersStatus } {
    let t: PeopleTab;
    let s: UsersStatus = 'active';
    if (rawT === 'everyone' || rawT === 'all' || rawT === 'users' || rawT === '') {
      t = 'users';
    } else if (rawT === 'deactivated') {
      t = 'users';
      s = 'inactive';
    } else if (rawT === 'invites' || rawT === 'invitations') {
      t = 'invitations';
    } else if (
      rawT === 'requests' ||
      rawT === 'approvals' ||
      rawT === 'awaiting-approval' ||
      rawT === 'awaiting_approval'
    ) {
      t = 'requests';
    } else {
      t = 'users';
    }
    if (t === 'users' && (rawS === 'active' || rawS === 'inactive')) {
      s = rawS;
    }
    return { tab: t, status: s };
  }
  const { tab, status: usersStatus } = normalizePeople(rawTab, rawStatus);

  function selectTab(next: PeopleTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'users') params.delete('tab');
    else params.set('tab', next);
    // Reset status filter when leaving Users tab so it doesn't linger.
    if (next !== 'users') params.delete('status');
    setSearchParams(params, { replace: true });
  }

  function selectUsersStatus(next: UsersStatus) {
    const params = new URLSearchParams(searchParams);
    // Users tab is default so we don't set ?tab= here.
    if (next === 'active') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  }

  const pendingInvites = invitations.filter((i) => i.status === 'pending').length;
  const deactivatedMembers = members?.filter((m) => !m.is_active) ?? [];
  const activeMembers = members?.filter((m) => m.is_active) ?? [];

  // Sub-agent UUUUU (2026-07-31) — per-user usage lookup + hero metrics.
  // Index once per usageByUser change so the O(rows) map builds don't run
  // on every table render.
  const usageByAccountUserId = useMemo(
    () => new Map(usageByUser.map((r) => [r.account_user_id, r] as const)),
    [usageByUser],
  );

  const userHeroMetrics = useMemo(() => {
    const total = activeMembers.length;
    // Active-this-month = users with credit spend OR any dispatch in usage.
    const activeThisMonth = usageByUser.filter(
      (r) => Number(r.credits_used_period) > 0 || r.dispatches_period > 0,
    ).length;
    const spendSum = usageByUser.reduce(
      (acc, r) => acc + Number(r.credits_used_period),
      0,
    );
    const avgSpend =
      activeThisMonth > 0 ? Math.round(spendSum / activeThisMonth) : 0;
    // Users at/near cap = members with a monthly_hard_cap_credits set AND
    // >= 80% consumed this month. We approximate consumption via the
    // usage row (credits_used_period matches this_month). NULL cap =
    // Unlimited (not at cap by definition).
    const usersAtCap = activeMembers.filter((m) => {
      if (m.monthly_hard_cap_credits === null || m.monthly_hard_cap_credits === '') {
        return false;
      }
      const cap = Number(m.monthly_hard_cap_credits);
      if (!Number.isFinite(cap) || cap <= 0) return false;
      const usage = usageByAccountUserId.get(m.account_user_id);
      const used = usage === undefined ? 0 : Number(usage.credits_used_period);
      return used / cap >= 0.8;
    }).length;
    return {
      total,
      activeThisMonth,
      activePct:
        total > 0 ? Math.round((activeThisMonth / total) * 100) : 0,
      avgSpend,
      usersAtCap,
    };
  }, [activeMembers, usageByUser, usageByAccountUserId]);

  return (
    <div>
      <div className="lightning-highlight">
        <div className="lightning-highlight-left">
          <ObjectIcon kind="people" size="lg" />
          <div className="lightning-highlight-text">
            <h1 className="lightning-title">People</h1>
            <p
              className="muted"
              style={{ margin: '6px 0 0', fontSize: '13px', maxWidth: '72ch', lineHeight: 1.45 }}
            >
              Your team, invitations, and requests awaiting your decision.
            </p>
          </div>
        </div>
      </div>

      {/* Push #38. Setup-shell. Tabs + tab content render as ONE card so
         the strip doesn't float on the grey page bg (the "boxes look
         disconnected" problem Ean flagged 2026-06-11). Advanced identity
         renders as its own card below. it's conceptually separate
         (power-user controls, not part of the users list). */}
      <div className="setup-shell">
      <div className="setup-shell-tabs">
        <button
          type="button"
          className={tab === 'users' ? 'lightning-tab active' : 'lightning-tab'}
          onClick={() => selectTab('users')}
        >
          Users{members !== null && ` (${activeMembers.length})`}
        </button>
        <button
          type="button"
          className={tab === 'invitations' ? 'lightning-tab active' : 'lightning-tab'}
          onClick={() => selectTab('invitations')}
        >
          Invitations{pendingInvites > 0 && ` (${pendingInvites})`}
        </button>
        <button
          type="button"
          className={tab === 'requests' ? 'lightning-tab active' : 'lightning-tab'}
          onClick={() => selectTab('requests')}
        >
          Requests
        </button>
      </div>

      {tab === 'invitations' && (
      <section style={{ marginBottom: 'var(--card-gap)' }}>
        <h2 className="section-title">Invite a teammate</h2>
        <form
          onSubmit={handleInvite}
          className="card card-padded"
          style={{
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <input
            type="email"
            required
            autoComplete="off"
            placeholder="teammate@company.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="input"
            style={{ flex: 1, minWidth: '240px' }}
          />
          <select
            value={inviteRole}
            onChange={(e) => setInviteRole(e.target.value as 'admin' | 'user')}
            className="select"
            style={{ width: '140px' }}
            aria-label="Role"
          >
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
          {/* Push 70b (D-198 wallet collapse): invite-time policy picker
             retired. Every new user joins on the single workspace credits
             model; the admin tunes tags + spend caps on the People list
             after the invite is accepted. */}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={inviting || inviteEmail.trim() === ''}
          >
            {inviting ? 'Sending…' : 'Send invite'}
          </button>
        </form>
      </section>
      )}

      {tab === 'users' && (
      <section style={{ marginBottom: 'var(--card-gap)' }}>
        {/* Sub-agent UUUUU (2026-07-31) — hero metric strip. Four terse
           numeric tiles above the list so the admin sees the workspace's
           people health at a glance: total, active this month, avg spend,
           users near their cap. Deliberately compact — no visual heaviness
           per Permissions Assignments precedent. */}
        {members !== null && members.length > 0 && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
              gap: 10,
              marginBottom: 'var(--card-gap)',
            }}
          >
            <PeopleHeroMetric
              label="Total users"
              value={String(userHeroMetrics.total)}
            />
            <PeopleHeroMetric
              label="Active this month"
              value={`${userHeroMetrics.activeThisMonth}`}
              {...(userHeroMetrics.total > 0
                ? { caption: `${userHeroMetrics.activePct}% of team` }
                : {})}
            />
            <PeopleHeroMetric
              label="Avg spend / active user"
              value={userHeroMetrics.avgSpend.toLocaleString()}
              caption="credits · this month"
            />
            <PeopleHeroMetric
              label="Users at / near cap"
              value={String(userHeroMetrics.usersAtCap)}
              {...(userHeroMetrics.usersAtCap > 0
                ? { caption: '≥ 80% consumed' }
                : {})}
              tone={userHeroMetrics.usersAtCap > 0 ? 'warn' : 'default'}
            />
          </div>
        )}
        {/* Push 81 (2026-07-23) — Users tab migrated to <ListViewShell
           variant='embedded'>. Shell owns the parallel title + helper on
           the left and search + filter (+ optional "Set caps by tag"
           action) on the right of a single header row.
             - Bulk-actions sticky bar: owner-only, opts in via bulkSelect
               prop. Actions match Push 66 shape: PS-assign group + role-
               change group (extraContent) + Deactivate (actions). Cancel
               auto-rendered by shell.
             - Skeleton loading: shell renders SkeletonTable when loading=true
               (matches useDelayedFlag existing behavior via showSkeleton).
           Table body + empty-state ("Just you here so far" / colspan
           "Nothing matched" caption) stay consumer-owned — the table
           columns, inline name-edit, inline role dropdown, tag chip
           editor are all surface-specific spice.
           Sub-agent RRRRR (2026-07-31) — Active/Inactive `viewSelector`
           pillbox killed; Status becomes a Status dimension inside the
           Filter dropdown. Active facet renders in the chip strip below
           the toolbar per Push 82 A.3. */}
        <ListViewShell
          variant="embedded"
          storageKey="list-view:admin-people"
          title="Users"
          helper="Your team. Owners and admins can invite people, change roles, and set spend limits."
          {...(isOwner && accountTags.length > 0
            ? {
                toolbarActions: (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setBulkCapModalOpen(true)}
                  >
                    Set caps by tag
                  </button>
                ),
              }
            : {})}
          search={{
            value: search,
            onChange: setSearch,
            placeholder: 'Search name, email, tag',
            ariaLabel: 'Filter members',
          }}
          filter={{
            menuId: 'people-users-filter-menu',
            activeCount:
              (usersStatus !== 'active' ? 1 : 0) +
              (roleFilter !== 'all' ? 1 : 0) +
              tagFilter.size +
              (capFilter !== 'all' ? 1 : 0),
            onReset: () => {
              selectUsersStatus('active');
              setRoleFilter('all');
              setCapFilter('all');
              setTagFilter(new Set());
              persistPeopleFilter({ role: 'all', cap: 'all' });
            },
            dimensions: (() => {
              const dims: ListFilterDimension[] = [
                {
                  key: 'status',
                  kind: 'single',
                  label: 'Status',
                  value: usersStatus,
                  onChange: (next: string) =>
                    selectUsersStatus(next as UsersStatus),
                  options: [
                    {
                      value: 'active',
                      label: 'Active',
                      ...(members !== null
                        ? { count: activeMembers.length }
                        : {}),
                    },
                    {
                      value: 'inactive',
                      label: 'Inactive',
                      ...(members !== null
                        ? { count: deactivatedMembers.length }
                        : {}),
                    },
                  ],
                },
                {
                  key: 'role',
                  kind: 'single',
                  label: 'Role',
                  value: roleFilter,
                  onChange: (next: string) => {
                    const casted = next as PeopleRoleFilter;
                    setRoleFilter(casted);
                    persistPeopleFilter({ role: casted });
                  },
                  options: [
                    { value: 'all', label: 'All roles' },
                    { value: 'owner', label: 'Owner' },
                    { value: 'admin', label: 'Admin' },
                    { value: 'user', label: 'User' },
                  ],
                },
                {
                  key: 'cap',
                  kind: 'single',
                  label: 'Spend limit',
                  value: capFilter,
                  onChange: (next: string) => {
                    const casted = next as PeopleCapFilter;
                    setCapFilter(casted);
                    persistPeopleFilter({ cap: casted });
                  },
                  options: [
                    { value: 'all', label: 'All' },
                    { value: 'custom', label: 'Custom cap' },
                    { value: 'unlimited', label: 'Unlimited' },
                  ],
                },
              ];
              if (accountTags.length > 0) {
                dims.push({
                  key: 'tags',
                  kind: 'multi',
                  label: 'Tags',
                  values: tagFilter,
                  onChange: setTagFilter,
                  options: accountTags.map((t) => ({
                    value: t,
                    label: t,
                  })),
                });
              }
              return dims;
            })(),
            activeFacets: [
              ...(usersStatus !== 'active'
                ? [
                    {
                      key: `status:${usersStatus}`,
                      label: `Status: Inactive`,
                      onRemove: () => selectUsersStatus('active'),
                    },
                  ]
                : []),
              ...(roleFilter !== 'all'
                ? [
                    {
                      key: `role:${roleFilter}`,
                      label: `Role: ${roleFilter[0]!.toUpperCase()}${roleFilter.slice(1)}`,
                      onRemove: () => {
                        setRoleFilter('all');
                        persistPeopleFilter({ role: 'all' });
                      },
                    },
                  ]
                : []),
              ...(capFilter !== 'all'
                ? [
                    {
                      key: `cap:${capFilter}`,
                      label: `Spend limit: ${capFilter === 'custom' ? 'Custom cap' : 'Unlimited'}`,
                      onRemove: () => {
                        setCapFilter('all');
                        persistPeopleFilter({ cap: 'all' });
                      },
                    },
                  ]
                : []),
              ...Array.from(tagFilter).map((t) => ({
                key: `tag:${t}`,
                label: `Tag: ${t}`,
                onRemove: () => {
                  const next = new Set(tagFilter);
                  next.delete(t);
                  setTagFilter(next);
                },
              })),
            ],
          }}
          {...(isOwner
            ? {
                bulkSelect: {
                  selectedIds,
                  onSelectionChange: setSelectedIds,
                  ...(bulkProgress !== null
                    ? {
                        progressCaption: `${bulkProgress.done} / ${bulkProgress.total} done…`,
                      }
                    : {}),
                  extraContent: (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexWrap: 'wrap',
                        }}
                      >
                        <label
                          className="muted"
                          style={{ fontSize: 12 }}
                          htmlFor="bulk-ps-select"
                        >
                          Assign to permission set:
                        </label>
                        <select
                          id="bulk-ps-select"
                          className="form-input olytic-select--sm"
                          value={bulkPsId}
                          onChange={(e) => setBulkPsId(e.target.value)}
                          disabled={bulkBusy || permissionSets.length === 0}
                          style={{
                            fontSize: 12.5,
                            minWidth: 180,
                          }}
                        >
                          <option value="">
                            {permissionSets.length === 0
                              ? 'No permission sets'
                              : 'Pick a set…'}
                          </option>
                          {permissionSets.map((ps) => (
                            <option key={ps.id} value={ps.id}>
                              {ps.name}
                            </option>
                          ))}
                        </select>
                        <LoadingButton
                          type="button"
                          className="btn btn-secondary"
                          loading={bulkBusy && bulkPsId !== ''}
                          loadingLabel="Assigning…"
                          disabled={bulkBusy || bulkPsId === ''}
                          onClick={() => void handleBulkAssignToPS()}
                        >
                          Assign
                        </LoadingButton>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          flexWrap: 'wrap',
                        }}
                      >
                        <label className="muted" style={{ fontSize: 12 }}>
                          Change role:
                        </label>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBulkRoleTarget('admin')}
                          disabled={bulkBusy}
                          style={{ fontSize: 12.5 }}
                        >
                          Make admin
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBulkRoleTarget('user')}
                          disabled={bulkBusy}
                          style={{ fontSize: 12.5 }}
                        >
                          Make user
                        </button>
                      </div>
                    </>
                  ),
                  actions: [
                    {
                      label: 'Deactivate',
                      danger: true,
                      disabled: bulkBusy,
                      onClick: () => setBulkDeactivateOpen(true),
                    },
                  ],
                },
              }
            : {})}
        >
        {/* Push 4-1e-cleanup-fu2 (2026-07-08): "What do the policies mean?"
            Collapsible removed from the Users tab. It read out-of-place
            as a dev-y row during Ean's smoke walk. The Policy column header
            below now carries a HelpIcon tooltip pointing to the plain-speak
            labels; the same explainer still lives on the Billing People tab
            for deeper policy configuration. Single source of truth stays in
            walletPolicy.ts. */}
        {members === null ? (
          showSkeleton ? <SkeletonTable rows={3} cols={6} /> : <div style={{ minHeight: '120px' }} />
        ) : (usersStatus === 'inactive' ? deactivatedMembers : activeMembers).length === 0 ? (
          <EmptyState
            kind="people"
            title={
              usersStatus === 'inactive'
                ? 'No inactive members.'
                : 'Just you here so far.'
            }
            body={
              usersStatus === 'inactive'
                ? 'Freezing or removing a user moves them here.'
                : 'Invite a teammate to get started.'
            }
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {/* Round 6.5 Item 31. bulk-select checkbox column.
                     Owner-only (matches existing role-edit + policy-edit
                     gating below). The header checkbox toggles all
                     filtered, non-self rows; partial state shows as
                     unchecked. */}
                  {isOwner && (
                    <th style={{ width: 32 }}>
                      {(() => {
                        const eligible =
                          members === null
                            ? []
                            : members.filter(
                                (m) => m.account_user_id !== user!.user_id,
                              );
                        const selectedEligible = eligible.filter((m) =>
                          selectedIds.has(m.account_user_id),
                        );
                        const allChecked =
                          eligible.length > 0 &&
                          selectedEligible.length === eligible.length;
                        const someChecked =
                          selectedEligible.length > 0 && !allChecked;
                        return (
                          <Checkbox
                            aria-label="Select all"
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={(next) => {
                              if (next) {
                                setSelectedIds(
                                  new Set(eligible.map((m) => m.account_user_id)),
                                );
                              } else {
                                clearSelected();
                              }
                            }}
                            disabled={bulkBusy}
                          />
                        );
                      })()}
                    </th>
                  )}
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  {/* Push 70b (D-198 wallet collapse): Wallet Policy column
                      retired; every user is on team-pot. Balance column
                      retired; individual bucket is gone. Tags column
                      replaces both with a governance affordance that
                      admins actually use (reporting groupBy + bulk cap).
                      Sub-agent UUUUU (2026-07-31): Connectors + Runs
                      columns dropped from the list. They surface on the
                      per-user detail page (PersonDetail's Connectors +
                      Activity tabs). Replaced with governance-relevant
                      columns: Spend this month (with cap indicator) +
                      Last active (relative time). Same total column
                      count so the header layout stays balanced. */}
                  <th style={{ minWidth: 220 }}>Tags</th>
                  <th>Status</th>
                  <th className="col-num">Spend</th>
                  <th>Last active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const q = search.trim().toLowerCase();
                  // Push 4-1e-cleanup-fu2 (2026-07-08): the split still exists
                  // but the trigger moved from the deleted Deactivated tab
                  // onto the Active/Inactive status chip filter above the
                  // table. Default is Active; Inactive shows deactivated
                  // members inline (same table, same actions, same drill).
                  const roster = usersStatus === 'inactive' ? deactivatedMembers : activeMembers;
                  // Push 70b (D-198): filter matches display name + email +
                  // tags (any). Tag filter chips narrow further via AND.
                  const tagLower = new Set(
                    Array.from(tagFilter).map((t) => t.toLowerCase()),
                  );
                  const filtered = roster.filter((m) => {
                    const memberTags = (m.tags ?? []).map((t) => t.toLowerCase());
                    // Push 79 (2026-07-23) — Role dimension (filter dropdown).
                    if (roleFilter !== 'all' && m.role !== roleFilter) return false;
                    // Push 79 (2026-07-23) — Spend-limit dimension.
                    const hasCap =
                      m.monthly_hard_cap_credits !== null &&
                      m.monthly_hard_cap_credits !== '';
                    if (capFilter === 'custom' && !hasCap) return false;
                    if (capFilter === 'unlimited' && hasCap) return false;
                    if (tagLower.size > 0) {
                      // All active-filter tags must be present on the row.
                      for (const t of tagLower) {
                        if (!memberTags.includes(t)) return false;
                      }
                    }
                    if (q === '') return true;
                    if (m.display_name.toLowerCase().includes(q)) return true;
                    if (m.email.toLowerCase().includes(q)) return true;
                    if (memberTags.some((t) => t.includes(q))) return true;
                    return false;
                  });
                  if (filtered.length === 0) {
                    // Push 70b (D-198): colspan updated for the new column
                    // layout — Name / Email / Role / Tags / Status /
                    // Connectors / Runs / kebab = 8 (or 9 with checkbox).
                    return (
                      <tr>
                        <td
                          colSpan={isOwner ? 9 : 8}
                          className="muted"
                          style={{ padding: '20px', textAlign: 'center' }}
                        >
                          {/* Push 79 (2026-07-23) — combined empty-state
                             message accounts for search + filter-dropdown
                             (role / spend limit / tags) together. */}
                          {search.trim() === '' ? (
                            <>No users match the current filters.</>
                          ) : (
                            <>
                              Nothing matched "{search.trim()}"
                              {(roleFilter !== 'all' ||
                                capFilter !== 'all' ||
                                tagFilter.size > 0) &&
                                ' with the current filters'}
                              .
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  }
                  return filtered.map((m) => {
                    const isMe = m.account_user_id === user.user_id;
                    const isEditingName = editingNameId === m.account_user_id;
                    const canChangeRole = isOwner && !isMe && m.role !== 'owner';
                    // Push 51 (2026-07-15, Ean bug report) — whole-row click
                    // navigates to /admin/people/:short_id.
                    // Push 81 (2026-07-23) — migrated to <ListRow>. Row-click
                    // drill + interactive-descendant bail-out + checkbox td
                    // (owner-only) + kebab td are owned by the primitive.
                    // isEditingName gate stays inline so clicks outside the
                    // input (e.g., on the Email cell) don't drill while a
                    // rename is in flight.
                    return (
                      <ListRow
                        key={m.account_user_id}
                        cursorMode={isEditingName ? 'default' : 'pointer'}
                        onRowClick={() => {
                          if (isEditingName) return;
                          navigate(`/admin/people/${m.short_id}`);
                        }}
                        {...(isOwner
                          ? {
                              bulkSelectId: m.account_user_id,
                              bulkSelected: selectedIds.has(m.account_user_id),
                              onBulkSelectChange: () =>
                                toggleSelected(m.account_user_id),
                              bulkSelectDisabled: isMe || bulkBusy,
                              bulkSelectAriaLabel: `Select ${m.display_name}`,
                            }
                          : {})}
                        kebabActions={buildMemberActions(m)}
                      >
                        <td>
                          {/* Inline name edit. Click the name → input swap.
                             Enter or blur saves; Escape cancels. Locked for
                             yourself (use /profile) and for owners by non-owners. */}
                          {isEditingName ? (
                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                              <input
                                type="text"
                                className="form-input"
                                value={nameDraft}
                                onChange={(e) => setNameDraft(e.target.value)}
                                onBlur={() => void handleSaveName(m)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void handleSaveName(m);
                                  }
                                  if (e.key === 'Escape') {
                                    setEditingNameId(null);
                                  }
                                }}
                                autoFocus
                                maxLength={128}
                                disabled={busy === m.account_user_id}
                                style={{ padding: '4px 8px', fontSize: '13px', minWidth: '180px' }}
                              />
                            </div>
                          ) : (
                            <div className="cell-primary" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              {/* Push 4-1d smoke-fix (2026-07-02): primary cell is
                                 now a RecordLink to /admin/people/:short_id so
                                 the row is drill-navigable at first glance.
                                 Rename affordance moves to an owner-only pencil
                                 button next to the link (previous behavior was
                                 "click the name → input swap" which conflicted
                                 with the drill target). */}
                              <RecordLink
                                to={`/admin/people/${m.short_id}`}
                                title="Open user record"
                              >
                                {m.display_name}
                              </RecordLink>
                              {isMe && (
                                <span className="muted" style={{ marginLeft: '2px', fontWeight: 400, fontSize: '12px' }}>
                                  (you)
                                </span>
                              )}
                              {!isMe && isOwner && (
                                <button
                                  type="button"
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => {
                                    setEditingNameId(m.account_user_id);
                                    setNameDraft(m.display_name);
                                  }}
                                  title="Rename user"
                                  aria-label={`Rename ${m.display_name}`}
                                  style={{ padding: '2px 6px', fontSize: '11px', color: 'var(--fg-muted)' }}
                                >
                                  Rename
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="muted">{m.email}</td>
                        <td>
                          {/* Inline role dropdown for owners on non-owners.
                             Owner role shown as RoleBadge (locked); self
                             shown as RoleBadge (no self-role-change). */}
                          {canChangeRole ? (
                            <select
                              value={m.role}
                              onChange={(e) =>
                                void handleInlineRoleChange(
                                  m,
                                  e.target.value as 'owner' | 'admin' | 'user',
                                )
                              }
                              disabled={busy === m.account_user_id}
                              className="form-input olytic-select--sm"
                              style={{
                                fontSize: '12.5px',
                                minWidth: '110px',
                              }}
                              aria-label={`Change role for ${m.display_name}`}
                            >
                              <option value="user">User</option>
                              <option value="admin">Admin</option>
                              <option value="owner">Owner</option>
                            </select>
                          ) : (
                            <RoleBadge role={m.role} />
                          )}
                        </td>
                        <td>
                          {/* Push 70b (D-198 wallet collapse): Tags cell.
                             Owners see the full chip editor with typeahead;
                             non-owners see a compact read-only chip strip
                             (governance edits stay owner-scoped). */}
                          {isOwner ? (
                            inlineTagEditRow === m.account_user_id ? (
                              <TagChipEditor
                                value={m.tags ?? []}
                                suggestions={accountTags}
                                onChange={(next) => {
                                  void handleUpdateTags(m, next);
                                }}
                                placeholder="Add tag…"
                                ariaLabel={`Tags for ${m.display_name}`}
                                disabled={busy === m.account_user_id}
                              />
                            ) : (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInlineTagEditRow(m.account_user_id);
                                }}
                                aria-label={`Edit tags for ${m.display_name}`}
                                style={{
                                  display: 'inline-flex',
                                  gap: 4,
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                  padding: '2px 4px',
                                  minHeight: 24,
                                  background: 'transparent',
                                  border: '1px dashed transparent',
                                  borderRadius: 4,
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor =
                                    'var(--border)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor =
                                    'transparent';
                                }}
                              >
                                {(m.tags ?? []).length === 0 ? (
                                  <span
                                    className="muted"
                                    style={{ fontSize: 12 }}
                                  >
                                    + tag
                                  </span>
                                ) : (
                                  (m.tags ?? []).slice(0, 3).map((t) => (
                                    <span
                                      key={t}
                                      style={{
                                        display: 'inline-block',
                                        padding: '2px 6px',
                                        fontSize: 11,
                                        color: 'var(--olytic-obsidian)',
                                        background: 'var(--olytic-obsidian-08)',
                                        border: '1px solid var(--border)',
                                        borderRadius: 999,
                                        lineHeight: 1.4,
                                      }}
                                    >
                                      {t}
                                    </span>
                                  ))
                                )}
                                {(m.tags ?? []).length > 3 && (
                                  <span
                                    className="muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    +{(m.tags ?? []).length - 3}
                                  </span>
                                )}
                              </button>
                            )
                          ) : (m.tags ?? []).length === 0 ? (
                            <span className="muted" style={{ fontSize: 12 }}>
                              —
                            </span>
                          ) : (
                            <span
                              style={{
                                display: 'inline-flex',
                                gap: 4,
                                flexWrap: 'wrap',
                              }}
                            >
                              {(m.tags ?? []).map((t) => (
                                <span
                                  key={t}
                                  style={{
                                    padding: '2px 6px',
                                    fontSize: 11,
                                    color: 'var(--fg-muted)',
                                    background: 'var(--olytic-obsidian-08)',
                                    borderRadius: 999,
                                  }}
                                >
                                  {t}
                                </span>
                              ))}
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {m.is_active ? (
                              <Pill kind="success" dot>Active</Pill>
                            ) : (
                              <Pill kind="danger">Deactivated</Pill>
                            )}
                            {!m.email_verified && <Pill kind="warning" dot>Unverified</Pill>}
                          </span>
                        </td>
                        <td className="col-num">
                          {(() => {
                            // Sub-agent UUUUU (2026-07-31) — Spend cell.
                            // Reads from the usage rollup fetched in reload();
                            // renders "—" when the user has no rows this
                            // month, and a colored strip when they're
                            // near their cap.
                            const usage = usageByAccountUserId.get(
                              m.account_user_id,
                            );
                            const used =
                              usage === undefined
                                ? 0
                                : Number(usage.credits_used_period);
                            const cap =
                              m.monthly_hard_cap_credits === null ||
                              m.monthly_hard_cap_credits === ''
                                ? null
                                : Number(m.monthly_hard_cap_credits);
                            const pctOfCap =
                              cap !== null && cap > 0
                                ? Math.min(100, Math.round((used / cap) * 100))
                                : null;
                            const nearCap = pctOfCap !== null && pctOfCap >= 80;
                            return (
                              <div style={{ textAlign: 'right' }}>
                                <div
                                  style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    fontSize: 12.5,
                                    color: nearCap
                                      ? 'rgb(184, 90, 34)'
                                      : 'var(--olytic-obsidian)',
                                    fontWeight: nearCap ? 600 : 500,
                                  }}
                                  title={
                                    cap !== null
                                      ? `${Math.round(used).toLocaleString()} of ${cap.toLocaleString()} credits (${pctOfCap ?? 0}%)`
                                      : `${Math.round(used).toLocaleString()} credits · unlimited`
                                  }
                                >
                                  {used > 0
                                    ? Math.round(used).toLocaleString()
                                    : '—'}
                                </div>
                                {pctOfCap !== null && (
                                  <div
                                    className="muted"
                                    style={{
                                      fontSize: 10.5,
                                      fontVariantNumeric: 'tabular-nums',
                                    }}
                                  >
                                    {pctOfCap}% of cap
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td>
                          {(() => {
                            const usage = usageByAccountUserId.get(
                              m.account_user_id,
                            );
                            const iso = usage?.last_activity_at ?? null;
                            if (iso === null) {
                              return (
                                <span
                                  className="muted"
                                  style={{ fontSize: 12 }}
                                >
                                  —
                                </span>
                              );
                            }
                            return (
                              <span
                                className="muted"
                                style={{ fontSize: 12 }}
                                title={iso}
                              >
                                {formatRelativePeople(iso)}
                              </span>
                            );
                          })()}
                        </td>
                      </ListRow>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        )}
        </ListViewShell>
      </section>
      )}

      {tab === 'invitations' && (
      <section>
        {/* Push 82 (2026-07-23) — Invitations tab migrated to <ListViewShell
           variant='embedded'>. Renders inside the People setup-shell tab
           body; shell owns section-title header + toolbar (Status view-
           selector + search across email + inviter). Row semantics stay
           consumer-owned (RecordLink + status pill + kebab). Bulk-select
           NOT enabled per Push 66 discipline (Invitations doesn't need
           bulk — Resend/Delete are per-row actions). */}
        <InvitationsListSection
          invitations={invitations}
          recentlyResent={recentlyResent}
          busy={busy}
          onResend={handleResendInvite}
          onDelete={handleDeleteInvite}
        />
      </section>
      )}

      {tab === 'requests' && <AwaitingApprovalTab />}
      </div>
      {/* /setup-shell. close the unified card around tabs + tab content.
         Advanced identity below is its own separate card by design. */}

      {/* Push #84 round 15 (Ean dogfood 2026-06-21). "Advanced identity"
         Collapsible REMOVED. The section surfaced four "Coming later"
         teaser fields (SSO/SAML, SCIM, Bulk CSV invite, Inactive user
         cleanup) that had no behind-the-scenes code path. Ean's rule:
         capability-gated affordances (web search disabled on Haiku
         because the model lacks the capability) are legitimate; teaser
         features ("we're building this but it doesn't exist yet") are
         not. Each of these will ship as a real feature with a real
         page when the work lands. no placeholder surface in the
         meantime. Same discipline as Push #29's "Advanced controls"
         Collapsible removal from Settings.tsx (see comment there). */}

      {/* Round 6.5 Item 31. destructive-consequence confirmation for
         bulk role change. Per Round 6 ConfirmDialog pattern (not
         window.confirm). Body spells out the cascading impact so the
         operator confirms the blast radius before firing. */}
      {/* Push 70b (D-198 wallet collapse): TopupUserModal + bulk-topup
         modal retired. Individual allocation is gone; per-user cap edits
         live on PersonDetail's CreditsPoolCard, tag-scoped bulk caps
         live on the BulkCapByTagModal below. */}
      {bulkCapModalOpen && (
        <BulkCapByTagModal
          members={members ?? []}
          accountTags={accountTags}
          onClose={() => setBulkCapModalOpen(false)}
          onSaved={() => {
            setBulkCapModalOpen(false);
            void reload();
          }}
        />
      )}

      {/* Push 4-1ak (2026-07-10) — bulk deactivate confirm. Small type-
         to-confirm friction ("deactivate") since it's bulk; per-name
         type-to-confirm would be tedious. Owner rows + the operator's
         own row are skipped defensively so a mis-selected owner doesn't
         cascade. */}
      <ConfirmDialog
        open={bulkDeactivateOpen}
        title={`Deactivate ${selectedIds.size} ${selectedIds.size === 1 ? 'user' : 'users'}?`}
        body={`These users lose access immediately. Owners and your own account are skipped defensively. You can reactivate any of them later from the Inactive tab.`}
        impact={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>What happens on deactivate:</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>Their sessions end and they can't sign back in until reactivated</li>
              <li>Their connector authorizations become inactive</li>
              <li>Their permission-set memberships drop, grants restore on reactivate</li>
            </ul>
          </div>
        }
        confirmLabel={`Deactivate ${selectedIds.size}`}
        tone="danger"
        typeToConfirm="deactivate"
        onConfirm={() => void confirmBulkDeactivate()}
        onCancel={() => setBulkDeactivateOpen(false)}
      />

      <ConfirmDialog
        open={bulkRoleTarget !== null}
        title={`Change role to ${bulkRoleTarget ?? ''} for ${selectedIds.size} ${selectedIds.size === 1 ? 'user' : 'users'}?`}
        body={
          bulkRoleTarget === 'admin'
            ? `These users gain admin access. They can manage connectors, models, prompts, and billing. Owners and self-rows are skipped. This runs one at a time; partial failures will be reported.`
            : `These users lose admin privileges and revert to regular user access. Owners and self-rows are skipped. This runs one at a time; partial failures will be reported.`
        }
        confirmLabel={bulkRoleTarget === 'admin' ? 'Make admin' : 'Make user'}
        tone="danger"
        onConfirm={() => void confirmBulkRoleChange()}
        onCancel={() => setBulkRoleTarget(null)}
      />

      {/* Push 4-1aj (2026-07-10) — branded ConfirmDialog replaces the last
         three window.confirm() sites on People.tsx.

         Deactivate: destructive but reversible (reactivate action lives on
         the same row when tab=inactive). impact bullets spell out the
         cascading effects so the admin sees the blast radius before
         confirming — access loss + PS grant drops + audit note. */}
      <ConfirmDialog
        open={deactivateTarget !== null}
        title={`Deactivate ${deactivateTarget?.display_name ?? ''}?`}
        body={`They lose access to this workspace immediately. You can reactivate them from the Inactive tab any time.`}
        impact={
          deactivateTarget !== null ? (
            <div>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>What happens on deactivate:</div>
              <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                <li>Their session ends and they can't sign back in until reactivated</li>
                <li>Their {deactivateTarget.connector_count} connector{deactivateTarget.connector_count === 1 ? '' : 's'} become inactive</li>
                <li>Their permission-set memberships drop — grants restore on reactivate</li>
              </ul>
            </div>
          ) : undefined
        }
        confirmLabel="Deactivate"
        tone="danger"
        onConfirm={() =>
          deactivateTarget !== null ? void performDeactivate(deactivateTarget) : undefined
        }
        onCancel={() => setDeactivateTarget(null)}
      />

      {/* Promote to owner: high-blast-radius (billing + payment + workspace
         destruction rights). type-to-confirm on the display name so an
         accidental dropdown click doesn't grant owner power. */}
      <ConfirmDialog
        open={promoteOwnerTarget !== null}
        title={`Promote ${promoteOwnerTarget?.display_name ?? ''} to Owner?`}
        body={`Owners have full workspace control — billing, payment methods, and workspace-destruction rights. This can be reversed by another owner via the same role dropdown.`}
        impact={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>What an Owner can do:</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>Add and remove payment methods, buy credit packs, change plan</li>
              <li>Promote or demote other admins and owners</li>
              <li>Delete the workspace (via account controls)</li>
            </ul>
          </div>
        }
        {...(promoteOwnerTarget !== null ? { typeToConfirm: promoteOwnerTarget.display_name } : {})}
        confirmLabel="Promote to Owner"
        tone="danger"
        onConfirm={() =>
          promoteOwnerTarget !== null ? void performPromoteToOwner(promoteOwnerTarget) : undefined
        }
        onCancel={() => setPromoteOwnerTarget(null)}
      />

      {/* Delete invite: low-blast-radius (the invite link stops working;
         no user-visible history broken). No impact bullets — the body
         copy already spells out the consequence. */}
      <ConfirmDialog
        open={deleteInviteTarget !== null}
        title={`Delete invite for ${deleteInviteTarget?.email ?? ''}?`}
        body={`Removes the invitation and the link in their email stops working. You can send them a fresh invite any time from the form above.`}
        confirmLabel="Delete invite"
        tone="danger"
        onConfirm={() =>
          deleteInviteTarget !== null ? void performDeleteInvite(deleteInviteTarget) : undefined
        }
        onCancel={() => setDeleteInviteTarget(null)}
      />
    </div>
  );
}

/**
 * AwaitingApprovalTab — Push 4-1e-cleanup-fu (2026-07-08) unification;
 * nav flattened to a single filter row in Push 4-1ao (2026-07-11).
 *
 * Prior state (Push 4-1e): stacked two embedded surfaces
 * (AdminApprovals + AdminRequests) with their own filter bars + empty
 * states. Read like two features glued together, not one triage inbox.
 *
 * Interim state (Push 4-1e-cleanup-fu): ONE unified feed. Primary filter
 * (Awaiting me / All pending / Historical) rendered as a second-level
 * lightning-tab row; the Kind chip strip rendered as a third level.
 * That was three levels of nested nav inside a single tab — too many
 * per Ean's smoke walk.
 *
 * Current state (Push 4-1ao): the primary-filter tab strip is DELETED.
 * "Awaiting me / All pending / Historical" now renders as a Status
 * FilterChipGroup alongside the Kind FilterChipGroup in ONE filter row.
 * Two segmented controls, gap between them, wrap-on-narrow. The URL
 * param was renamed `filter` → `status` for cleaner naming; legacy
 * `?filter=X` values continue to be accepted at read time so deep links
 * still land on the right slice.
 *
 * Data sources (both admin-scoped; RLS Tier 2 admin/owner + account_id):
 *   - fetchAdminApprovalsList — billing__approval_request rows via
 *     /api/admin/approvals?filter={awaiting_me|all_pending|historical}.
 *   - fetchAdminRequests — sys__event_log request-shaped rows via
 *     /api/admin/requests?status={open|all}.
 *
 * Server contract preserved — this is a client-side merge; no new
 * endpoints. Each source is fetched with the filter that most closely
 * matches the active primary filter, then merged, then the kind chip
 * narrows further.
 *
 * Access-request resolve/reject affordance from the old AdminRequests
 * flow is preserved inline on rows where admin_actionable=true. Row
 * click on an approval drills to /admin/approvals/:short_id; row click
 * on a request drills to /admin/activity/:event_short_id.
 */
// Push 70b (D-198 wallet collapse): the "Approvals" tab is renamed to
// "Requests" and no longer merges billing__approval_request rows —
// that table was dropped in Migration 0158. The tab now only surfaces
// sys__event_log requests (access / suggestion / feedback / support).
type PrimaryFilter = 'awaiting_me' | 'all_pending' | 'historical';
type KindFilter = 'all' | 'access' | 'suggestion' | 'feedback' | 'support';

const PRIMARY_FILTERS: readonly PrimaryFilter[] = ['awaiting_me', 'all_pending', 'historical'];
const KIND_FILTERS: readonly KindFilter[] = ['all', 'access', 'suggestion', 'feedback', 'support'];

function primaryFilterLabel(f: PrimaryFilter): string {
  switch (f) {
    case 'awaiting_me': return 'Awaiting me';
    case 'all_pending': return 'All pending';
    case 'historical':  return 'Historical';
  }
}

function kindFilterLabel(k: KindFilter): string {
  switch (k) {
    case 'all':        return 'All kinds';
    case 'access':     return 'Access';
    case 'suggestion': return 'Suggestions';
    case 'feedback':   return 'Feedback';
    case 'support':    return 'Support';
  }
}

type RowKind = 'access' | 'suggestion' | 'feedback' | 'support';

interface UnifiedRow {
  key: string;
  kind: RowKind;
  status: string;
  actor_display_name: string;
  actor_short_id: string | null;
  title: string;
  body: string | null;
  requested_credits: string | null;
  template_name: string | null;
  template_display_name: string | null;
  admin_actionable: boolean;
  event_id: string | null;
  drill_to: string;
  created_at: string;
  resolved_at: string | null;
  resolver_display_name: string | null;
}

const ACCESS_REQUEST_TYPES = new Set(['connector_access', 'access_requested']);
const SUGGESTION_REQUEST_TYPES = new Set(['suggestion', 'new_connector', 'custom_connector']);

function requestTypeToKind(t: string): RowKind {
  if (ACCESS_REQUEST_TYPES.has(t)) return 'access';
  if (SUGGESTION_REQUEST_TYPES.has(t)) return 'suggestion';
  if (t === 'feedback') return 'feedback';
  if (t === 'support') return 'support';
  return 'suggestion';
}

function kindPillKind(k: RowKind): PillKind {
  switch (k) {
    case 'access':   return 'info';
    case 'support':  return 'warning';
    default:         return 'neutral';
  }
}

function kindLabel(k: RowKind): string {
  switch (k) {
    case 'access':     return 'Access';
    case 'suggestion': return 'Suggestion';
    case 'feedback':   return 'Feedback';
    case 'support':    return 'Support';
  }
}

function statusPillKind(s: string): PillKind {
  if (s === 'pending' || s === 'open') return 'warning';
  if (s === 'granted' || s === 'resolved') return 'success';
  if (s === 'denied' || s === 'rejected') return 'danger';
  return 'neutral';
}

function isPendingStatus(s: string): boolean {
  return s === 'pending' || s === 'open';
}

function emptyStateBody(primary: PrimaryFilter, kind: KindFilter): { title: string; body: string } {
  if (kind !== 'all') {
    // Narrower-slice empty states — the kind chip added a filter that eliminated everything.
    return {
      title: `Nothing in ${kindFilterLabel(kind).toLowerCase()} right now`,
      body: 'Clear the kind filter to see everything in this view.',
    };
  }
  switch (primary) {
    case 'awaiting_me':
      return { title: 'Nothing needs your attention right now.', body: 'Requests routed to you show up here.' };
    case 'all_pending':
      return { title: "No open requests. Everything's been handled.", body: 'New requests from your team land here first.' };
    case 'historical':
      return { title: 'No resolved requests yet.', body: 'Once you approve, deny, or resolve requests, they land here.' };
  }
}

function AwaitingApprovalTab() {
  const { user } = useSession();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Push 4-1ao (2026-07-11): the primary filter URL param was renamed
  // `?filter=` → `?status=` for cleaner naming (matches the segmented-
  // control label "Status:"). Legacy `?filter=X` reads continue to work
  // via a fallback so external deep links still resolve.
  const rawStatus = searchParams.get('status') ?? searchParams.get('filter') ?? 'awaiting_me';
  const primary: PrimaryFilter = (PRIMARY_FILTERS as readonly string[]).includes(rawStatus)
    ? (rawStatus as PrimaryFilter)
    : 'awaiting_me';

  const rawKind = searchParams.get('kind') ?? 'all';
  const kindFilter: KindFilter = (KIND_FILTERS as readonly string[]).includes(rawKind)
    ? (rawKind as KindFilter)
    : 'all';

  // Push 70b (D-198 wallet collapse): approvals state retired — the
  // billing__approval_request table was dropped in Migration 0158.
  const [requests, setRequests] = useState<
    Awaited<ReturnType<typeof fetchAdminRequests>>['requests'] | null
  >(null);
  const [instances, setInstances] = useState<AdminConnectorInstance[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  // Sub-agent VVVVV (2026-07-31) — Requests carried filter but no search;
  // the reference SettingsUsage list-view has BOTH. Search matches requester
  // display name + subject/body text + template display name (case-insensitive).
  const [search, setSearch] = useState('');
  // Push 4-1aj (2026-07-10) — branded ConfirmDialog state for the two
  // window.confirm() sites in the tab: install-and-grant (access request
  // that needs the connector installed first) and reject-request.
  const [installGrantTarget, setInstallGrantTarget] = useState<UnifiedRow | null>(null);
  const [rejectTarget, setRejectTarget] = useState<UnifiedRow | null>(null);

  // Track the primary filter so useEffect re-fetches when it changes but not
  // on kind chip changes (kind is client-side narrowing only).
  useEffect(() => {
    let cancelled = false;
    setRequests(null);
    setError(null);

    const requestStatusParam: 'open' | 'all' = primary === 'historical' ? 'all' : 'open';

    Promise.all([
      fetchAdminRequests({ status: requestStatusParam }).catch((err: unknown) => {
        throw err instanceof ApiError ? err : new Error('Could not load requests.');
      }),
      fetchAdminConnectors().catch(() => ({ instances: [] as AdminConnectorInstance[] })),
    ])
      .then(([reqRes, instRes]) => {
        if (cancelled) return;
        setRequests(reqRes.requests);
        setInstances(instRes.instances);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load items.');
      });

    return () => {
      cancelled = true;
    };
  }, [primary]);

  function selectPrimary(next: PrimaryFilter) {
    const params = new URLSearchParams(searchParams);
    // Push 4-1ao (2026-07-11): URL param renamed `filter` → `status`. On
    // write, always drop the legacy key so we don't leave stale
    // ?filter=… next to a fresh ?status=… in the URL bar.
    params.delete('filter');
    if (next === 'awaiting_me') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  }

  function selectKind(next: KindFilter) {
    const params = new URLSearchParams(searchParams);
    if (next === 'all') params.delete('kind');
    else params.set('kind', next);
    setSearchParams(params, { replace: true });
  }

  async function reload() {
    const requestStatusParam: 'open' | 'all' = primary === 'historical' ? 'all' : 'open';
    try {
      const [reqRes, instRes] = await Promise.all([
        fetchAdminRequests({ status: requestStatusParam }),
        fetchAdminConnectors(),
      ]);
      setRequests(reqRes.requests);
      setInstances(instRes.instances);
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Could not reload items.',
      });
    }
  }

  async function handleResolveAccess(row: UnifiedRow) {
    if (row.template_name === null || row.event_id === null) {
      showToast({ kind: 'error', message: 'This request has no connector to grant.' });
      return;
    }
    const inst = instances.find((i) => i.template_name === row.template_name);
    if (inst === undefined) {
      // Push 4-1aj (2026-07-10) — install-then-grant flow goes through the
      // branded ConfirmDialog so the admin sees the two-step consequence
      // (install to their workspace + grant to the requester) explicitly.
      // performInstallAndGrant() runs the install + grant after confirm.
      setInstallGrantTarget(row);
      return;
    }
    try {
      await resolveAccessRequest(row.event_id, 'granted', inst.instance_id);
      showToast({
        kind: 'success',
        message: `Granted ${row.actor_display_name} access to ${row.template_display_name ?? row.template_name}.`,
      });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Grant failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  async function performInstallAndGrant(row: UnifiedRow) {
    setInstallGrantTarget(null);
    if (row.template_name === null || row.event_id === null) return;
    setBusy(row.event_id);
    let inst: AdminConnectorInstance | undefined;
    try {
      const ins = await installConnector(row.template_name);
      const fresh = await fetchAdminConnectors();
      setInstances(fresh.instances);
      inst = fresh.instances.find((i) => i.instance_id === ins.instance_id);
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Install failed.',
      });
      setBusy(null);
      return;
    }
    if (inst === undefined) {
      showToast({ kind: 'error', message: 'Could not locate the installed connector.' });
      setBusy(null);
      return;
    }
    try {
      await resolveAccessRequest(row.event_id, 'granted', inst.instance_id);
      showToast({
        kind: 'success',
        message: `Granted ${row.actor_display_name} access to ${row.template_display_name ?? row.template_name}.`,
      });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Grant failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  function handleRejectAccess(row: UnifiedRow) {
    if (row.event_id === null) return;
    // Push 4-1aj (2026-07-10) — reject-request opens the branded
    // ConfirmDialog. performRejectAccess() runs the denial after confirm.
    setRejectTarget(row);
  }

  async function performRejectAccess(row: UnifiedRow) {
    setRejectTarget(null);
    if (row.event_id === null) return;
    setBusy(row.event_id);
    try {
      await resolveAccessRequest(row.event_id, 'denied');
      showToast({ kind: 'success', message: 'Request rejected.' });
      await reload();
    } catch (err) {
      showToast({
        kind: 'error',
        message: err instanceof ApiError ? err.message : 'Reject failed.',
      });
    } finally {
      setBusy(null);
    }
  }

  const merged: UnifiedRow[] | null = useMemo(() => {
    if (requests === null) return null;

    // Push 70b (D-198): approval rows retired. Only sys__event_log
    // requests (access / suggestion / feedback / support) render here.

    const requestRows: UnifiedRow[] = requests.map((r) => {
      const kind = requestTypeToKind(r.request_type);
      const title = kind === 'access' && r.template_display_name !== null
        ? `${r.requester_display_name} wants ${r.template_display_name}`
        : r.subject !== null && r.subject !== ''
          ? r.subject
          : `${r.requester_display_name} sent ${kindLabel(kind).toLowerCase()}`;
      const drillTo =
        r.event_short_id !== undefined && r.event_short_id !== null && r.event_short_id !== ''
          ? `/admin/activity/${r.event_short_id}`
          : `/admin/analytics/activity`;
      return {
        key: `request-${r.event_id}`,
        kind,
        status: r.status,
        actor_display_name: r.requester_display_name,
        actor_short_id: null,
        title,
        body: r.body,
        requested_credits: null,
        template_name: r.template_name,
        template_display_name: r.template_display_name,
        admin_actionable: r.admin_actionable,
        event_id: r.event_id,
        drill_to: drillTo,
        created_at: r.created_at,
        resolved_at: r.resolved_at,
        resolver_display_name: r.resolved_by_display_name,
      };
    });

    // For the requests source, apply the same primary filter narrowing that
    // the approvals server already applied. Server uses status=open|all — we
    // additionally narrow "awaiting_me" (pending + not self-authored) and
    // "historical" (non-open status) here on the client since the requests
    // endpoint doesn't have an "awaiting_me" mode.
    const filteredRequestRows = requestRows.filter((row) => {
      if (primary === 'awaiting_me') {
        // Approvals endpoint self-excludes on the server. The requests
        // endpoint doesn't have that mode; we can't safely infer "self"
        // from the returned rows (no requester user_id column on the wire).
        // Falling back to isPendingStatus is the closest match; the
        // requester-self edge case is a defensive gap the server would
        // need a new mode to close. Documented in the endpoint comment.
        return isPendingStatus(row.status);
      }
      if (primary === 'all_pending') {
        return isPendingStatus(row.status);
      }
      // historical
      return !isPendingStatus(row.status);
    });

    const all = [...filteredRequestRows];
    all.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : 0));
    return all;
  }, [requests, primary]);

  const filteredRows: UnifiedRow[] | null = useMemo(() => {
    if (merged === null) return null;
    const q = search.trim().toLowerCase();
    return merged.filter((r) => {
      if (kindFilter !== 'all' && r.kind !== kindFilter) return false;
      if (q === '') return true;
      if (r.actor_display_name.toLowerCase().includes(q)) return true;
      if (r.title.toLowerCase().includes(q)) return true;
      if (r.body !== null && r.body.toLowerCase().includes(q)) return true;
      if (
        r.template_display_name !== null &&
        r.template_display_name.toLowerCase().includes(q)
      ) {
        return true;
      }
      return false;
    });
  }, [merged, kindFilter, search]);

  const counts = useMemo(() => {
    const base = { all: 0, access: 0, suggestion: 0, feedback: 0, support: 0 };
    if (merged === null) return base;
    for (const r of merged) {
      base.all += 1;
      base[r.kind] += 1;
    }
    return base;
  }, [merged]);

  if (user === null) return null;

  const activeCount =
    (primary !== 'awaiting_me' ? 1 : 0) + (kindFilter !== 'all' ? 1 : 0);

  return (
    <ListViewShell
      variant="embedded"
      storageKey="list-view:admin-people-requests"
      title="Requests"
      helper="Access requests, feedback, and support from your team. Resolve, reject, or route to Olytic."
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search requests',
        ariaLabel: 'Search requests',
      }}
      filter={{
        menuId: 'people-requests-filter-menu',
        activeCount,
        onReset: () => {
          selectPrimary('awaiting_me');
          selectKind('all');
        },
        dimensions: [
          {
            key: 'status',
            kind: 'single',
            label: 'Status',
            value: primary,
            onChange: (next) => selectPrimary(next as PrimaryFilter),
            options: PRIMARY_FILTERS.map((key) => ({
              value: key,
              label: primaryFilterLabel(key),
            })),
          },
          {
            key: 'kind',
            kind: 'single',
            label: 'Kind',
            value: kindFilter,
            onChange: (next) => selectKind(next as KindFilter),
            options: KIND_FILTERS.map((key) => ({
              value: key,
              label: kindFilterLabel(key),
              ...(key !== 'all' && counts[key] > 0
                ? { count: counts[key] }
                : {}),
            })),
          },
        ],
        activeFacets: [
          ...(primary !== 'awaiting_me'
            ? [
                {
                  key: `status:${primary}`,
                  label: `Status: ${primaryFilterLabel(primary)}`,
                  onRemove: () => selectPrimary('awaiting_me'),
                },
              ]
            : []),
          ...(kindFilter !== 'all'
            ? [
                {
                  key: `kind:${kindFilter}`,
                  label: `Kind: ${kindFilterLabel(kindFilter)}`,
                  onRemove: () => selectKind('all'),
                },
              ]
            : []),
        ],
      }}
      loading={filteredRows === null && error === null}
      skeletonRows={4}
      skeletonCols={4}
      hasNoRows={filteredRows !== null && filteredRows.length === 0}
      empty={{
        kind: 'approval-request',
        title: emptyStateBody(primary, kindFilter).title,
        body: emptyStateBody(primary, kindFilter).body,
      }}
    >
      {error !== null && (
        <div className="callout callout-danger" style={{ margin: '12px' }}>
          <p className="callout-body">{error}</p>
        </div>
      )}

      {filteredRows !== null && filteredRows.length > 0 && (
        <div className="stack" style={{ padding: '4px 12px 12px' }}>
          {filteredRows.map((r) => (
            <article key={r.key} className="card card-padded inbox-card">
              <div className="inbox-card-body">
                <div className="inbox-card-avatar" aria-hidden="true">
                  {r.kind === 'access' && r.template_name !== null ? (
                    <BrandLogo
                      slug={r.template_name}
                      name={r.template_display_name ?? r.template_name}
                      size="md"
                    />
                  ) : (
                    <ObjectIcon kind="inbox" variant="soft" size="md" />
                  )}
                </div>
                <div className="inbox-card-main">
                  <div className="inbox-card-meta">
                    <Pill kind={kindPillKind(r.kind)}>{kindLabel(r.kind)}</Pill>
                    <Pill kind={statusPillKind(r.status)} dot>
                      {formatLabel(r.status)}
                    </Pill>
                    {/* Push 70b (D-198): requested_credits was surfaced
                       only on approval rows — those are retired. */}
                    <span className="muted inbox-card-time">
                      {formatTimestamp(r.created_at, user.timezone, user.account_timezone)}
                    </span>
                  </div>
                  <h3 className="inbox-card-title">
                    <RecordLink to={r.drill_to} title="Open record" style={{ color: 'var(--olytic-obsidian)' }}>
                      {r.title}
                    </RecordLink>
                  </h3>
                  <div className="muted" style={{ fontSize: '12px' }}>
                    From{' '}
                    {r.actor_short_id !== null ? (
                      <RecordLink
                        to={`/admin/people/${r.actor_short_id}`}
                        stopPropagation
                        title="Open person record"
                      >
                        {r.actor_display_name}
                      </RecordLink>
                    ) : (
                      r.actor_display_name
                    )}
                  </div>
                  {r.body !== null && r.body !== '' && (
                    <p className="inbox-card-quote">{r.body}</p>
                  )}
                  {r.resolved_at !== null && (
                    <div className="muted" style={{ fontSize: '12px' }}>
                      {r.resolver_display_name !== null ? `${r.resolver_display_name} · ` : ''}
                      resolved {formatTimestamp(r.resolved_at, user.timezone, user.account_timezone)}
                    </div>
                  )}
                </div>
                {r.admin_actionable && r.kind === 'access' && isPendingStatus(r.status) && (
                  <div className="inbox-card-actions">
                    <div className="button-row button-row-sm">
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={busy === r.event_id}
                        onClick={() => void handleResolveAccess(r)}
                      >
                        {busy === r.event_id ? 'Working…' : 'Resolve'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        disabled={busy === r.event_id}
                        onClick={() => void handleRejectAccess(r)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                )}
                {/* Push 70b (D-198 wallet collapse): approval-kind row
                   action retired. Approval workflow gone; only access
                   requests carry an inline action (Resolve/Reject
                   above). */}
              </div>
            </article>
          ))}
        </div>
      )}

      {/* Push 4-1aj (2026-07-10) — install-and-grant branded confirm. The
         Resolve action on an access request routes here when the connector
         isn't installed yet in this workspace. Two-step consequence (install
         then grant) surfaced in the impact bullets. */}
      <ConfirmDialog
        open={installGrantTarget !== null}
        title={`Install ${installGrantTarget?.template_display_name ?? installGrantTarget?.template_name ?? ''} and grant access?`}
        body={`${installGrantTarget?.template_display_name ?? installGrantTarget?.template_name ?? 'This connector'} isn't installed yet. Installing adds it to your workspace, then grants access to ${installGrantTarget?.actor_display_name ?? 'the requester'}.`}
        impact={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>What happens:</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
              <li>The connector is installed under your workspace (you can uninstall later from Connectors)</li>
              <li>{installGrantTarget?.actor_display_name ?? 'The requester'} is granted access immediately</li>
              <li>You can revoke access anytime from the connector's Assignments tab</li>
            </ul>
          </div>
        }
        confirmLabel="Install and grant"
        onConfirm={() =>
          installGrantTarget !== null ? void performInstallAndGrant(installGrantTarget) : undefined
        }
        onCancel={() => setInstallGrantTarget(null)}
      />

      {/* Push 4-1aj (2026-07-10) — reject-request branded confirm. Small-
         blast-radius (requester gets notified; they can re-request). No
         impact bullets needed — body copy carries the shape. */}
      <ConfirmDialog
        open={rejectTarget !== null}
        title={`Reject request from ${rejectTarget?.actor_display_name ?? ''}?`}
        body={`${rejectTarget?.actor_display_name ?? 'They'} will see the request as denied and can send another one if needed.`}
        confirmLabel="Reject"
        tone="danger"
        onConfirm={() =>
          rejectTarget !== null ? void performRejectAccess(rejectTarget) : undefined
        }
        onCancel={() => setRejectTarget(null)}
      />
    </ListViewShell>
  );
}

// Push 5-F (2026-07-11): SkeletonTable now lives in components/Skeleton.tsx
// (imported at the top of this file). Local def deleted — same visual, one
// source of truth.

/**
 * InvitationsListSection — Push 82 (2026-07-23) list-view unification.
 *
 * Embedded <ListViewShell> for the Invitations tab body. Shell owns section-
 * title header + toolbar (Status view-selector + search); consumer owns the
 * table columns + row kebab semantics (Resend / Delete). No bulk-select per
 * Push 66 discipline. RecordLink drill to /admin/invitations/:short_id
 * inherited from the anchor cell.
 */
interface InvitationsListSectionProps {
  invitations: AdminInvitation[];
  recentlyResent: Set<string>;
  busy: string | null;
  onResend: (inv: AdminInvitation) => Promise<void>;
  onDelete: (inv: AdminInvitation) => void;
}

function InvitationsListSection({
  invitations,
  recentlyResent,
  busy,
  onResend,
  onDelete,
}: InvitationsListSectionProps) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<
    'all' | 'pending' | 'accepted' | 'other'
  >('all');

  const pendingCount = invitations.filter((i) => i.status === 'pending').length;
  const acceptedCount = invitations.filter((i) => i.status === 'accepted').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invitations.filter((inv) => {
      if (statusFilter === 'pending' && inv.status !== 'pending') return false;
      if (statusFilter === 'accepted' && inv.status !== 'accepted') return false;
      if (
        statusFilter === 'other' &&
        (inv.status === 'pending' || inv.status === 'accepted')
      ) {
        return false;
      }
      if (q === '') return true;
      if (inv.email.toLowerCase().includes(q)) return true;
      if (
        inv.invited_by_display_name !== null &&
        inv.invited_by_display_name.toLowerCase().includes(q)
      ) {
        return true;
      }
      return false;
    });
  }, [invitations, search, statusFilter]);

  // Sub-agent KKKKK Track 2 (2026-07-29) — status preset pill-box
  // (All / Pending / Accepted) retired in favor of the canonical
  // Filter dropdown per Ean's list-view unification directive. Active
  // selection shows as a facet chip below the toolbar.
  function statusFacetLabel(v: typeof statusFilter): string {
    return v === 'pending'
      ? 'Pending'
      : v === 'accepted'
        ? 'Accepted'
        : 'Other';
  }

  return (
    <ListViewShell
      variant="embedded"
      storageKey="list-view:admin-people-invitations"
      title={`Invitations${pendingCount > 0 ? ` (${pendingCount} pending)` : ''}`}
      helper="Pending and accepted invites. Resend the email or delete an invite from its row kebab."
      filter={{
        ariaLabel: 'Filter invitations',
        activeCount: statusFilter !== 'all' ? 1 : 0,
        onReset: () => setStatusFilter('all'),
        dimensions: [
          {
            key: 'status',
            kind: 'single',
            label: 'Status',
            value: statusFilter,
            onChange: (next) =>
              setStatusFilter(next as 'all' | 'pending' | 'accepted' | 'other'),
            options: [
              { value: 'all', label: 'All', count: invitations.length },
              { value: 'pending', label: 'Pending', count: pendingCount },
              { value: 'accepted', label: 'Accepted', count: acceptedCount },
              { value: 'other', label: 'Other' },
            ],
          },
        ],
        activeFacets:
          statusFilter !== 'all'
            ? [
                {
                  key: `status:${statusFilter}`,
                  label: `Status: ${statusFacetLabel(statusFilter)}`,
                  onRemove: () => setStatusFilter('all'),
                },
              ]
            : [],
      }}
      search={{
        value: search,
        onChange: setSearch,
        placeholder: 'Search email or inviter',
        ariaLabel: 'Search invitations',
      }}
      hasNoRows={filtered.length === 0}
      empty={{
        kind: 'invitation',
        title:
          invitations.length === 0
            ? 'No pending invitations'
            : 'Nothing matches this filter',
        body:
          invitations.length === 0
            ? "Invite a teammate from the form above to send one. They'll get an email and can join in seconds."
            : 'Clear the search or status filter to see more.',
      }}
    >
      {filtered.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Role</th>
                <th>Invited by</th>
                <th>Status</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id}>
                  <td className="cell-primary">
                    <RecordLink
                      to={`/admin/invitations/${inv.short_id}`}
                      title="Open invitation record"
                    >
                      {inv.email}
                    </RecordLink>
                  </td>
                  <td>
                    <RoleBadge role={inv.invited_role} />
                  </td>
                  <td className="muted">{inv.invited_by_display_name ?? '-'}</td>
                  <td>
                    <span
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                    >
                      <InvitationStatusPill status={inv.status} />
                      {recentlyResent.has(inv.id) && (
                        <Pill kind="info">
                          <span aria-live="polite">Email sent</span>
                        </Pill>
                      )}
                    </span>
                  </td>
                  <td className="muted">
                    {/* Sub-agent UUUUU (2026-07-31) — relative-time
                       expiration ("Expires in 3 days"). Absolute date
                       lives in the row title for auditability. */}
                    <span
                      title={new Date(inv.expires_at).toLocaleString()}
                    >
                      {formatExpiresInPeople(inv.expires_at)}
                    </span>
                  </td>
                  <td className="col-right">
                    {/* Push #84 round 19. plain-speak actions: Resend
                       email sends a new link and the old one stops
                       working (PLAT-90 rotation); Delete invite hard-deletes
                       the row. Removed the dev-y "Revoke" label per
                       Ean dogfood 2026-06-22. Pending invites get both
                       actions; accepted invites get just the Delete
                       action so admins can clean up history. */}
                    <KebabMenu
                      actions={
                        inv.status === 'pending'
                          ? [
                              {
                                label: 'Resend email',
                                disabled: busy === inv.id,
                                onSelect: () => void onResend(inv),
                              },
                              {
                                label: 'Delete invite',
                                danger: true,
                                disabled: busy === inv.id,
                                onSelect: () => onDelete(inv),
                              },
                            ]
                          : [
                              {
                                label: 'Delete invite',
                                danger: true,
                                disabled: busy === inv.id,
                                onSelect: () => onDelete(inv),
                              },
                            ]
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </ListViewShell>
  );
}
